import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Mail, Phone, MapPin, Send, MessageSquare } from 'lucide-react';
import { showToast } from '../lib/utils';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://formspree.io/f/mvzloldw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        showToast("Message sent successfully! We'll get back to you soon.", 'success');

        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error(result?.error || 'Failed to send message');
      }
    } catch (error) {
      console.error(error);
      showToast('Failed to send message. Please try again or email us directly.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#111111]">
      <Navbar />

      <main className="flex-1 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex items-center gap-4 mb-8">
            <MessageSquare className="w-8 h-8 text-[#8B0000]" />
            <h1 className="text-4xl font-bold text-white">Contact Us</h1>
          </div>

          <div className="grid md:grid-cols-2 gap-8">

            {/* FORM */}
            <div className="bg-[#222222] rounded-lg p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Send us a Message</h2>

              <form onSubmit={handleSubmit} className="space-y-4">

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-[#8B0000] focus:ring focus:ring-[#8B0000]/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-[#8B0000] focus:ring focus:ring-[#8B0000]/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Subject
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-[#8B0000] focus:ring focus:ring-[#8B0000]/50"
                  >
                    <option value="">Select a subject</option>
                    <option value="Kids Camp Registration">Kids Camp Registration</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Sponsorship">Partner with Us!</option>
                    <option value="Media Relations">Media Relations</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    required
                    className="w-full px-4 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-[#8B0000] focus:ring focus:ring-[#8B0000]/50"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-6 py-3 bg-[#8B0000] text-white font-semibold rounded-lg hover:bg-[#A00000] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Send className="w-5 h-5" />
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

              </form>
            </div>

            {/* CONTACT INFO */}
            <div className="space-y-6">

              <div className="bg-[#222222] rounded-lg p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Contact Information</h2>

                <div className="space-y-6">

                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-[#8B0000]" />
                    <div>
                      <h3 className="text-lg font-semibold text-white">Location</h3>
                      <p className="text-gray-400">272 Gardiner Rd</p>
                      <p className="text-gray-400">Wiscasset, ME 04578</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Phone className="w-6 h-6 text-[#8B0000]" />
                    <div>
                      <h3 className="text-lg font-semibold text-white">Phone</h3>
                      <p className="text-gray-400">(207) 751-3271</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Mail className="w-6 h-6 text-[#8B0000]" />
                    <div>
                      <h3 className="text-lg font-semibold text-white">Email</h3>
                      <p className="text-gray-400">gobirds@lincolncountycardinals.com</p>
                    </div>
                  </div>

                </div>
              </div>

            </div>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;